import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import Footer from "../../../components/footer/footer";
import { useServiceProperty } from "../../../hooks/serviceProperty";
import { useServiceShoppingCart } from "../../../hooks/serviceShoppingCart";
import { AppProperty } from "../../../models/Property";
import { storageServerPath } from "../../../utils/server/api";
import Gallery from "../../../components/gallery/gallery";
import { useAuth } from "../../../context/AuthContext";
import ModalConfirmation from "../../../components/alerts/modalConfirmation";
import ModalConfirmation2 from "../../../components/alerts/modalConfirmation";
import { toast, ToastContainer } from "react-toastify";
import ModalArchive from "@/components/alerts/archive/ModalArchive";
import ModalUnarchive from "@/components/alerts/archive/ModalUnarchive";

import PropertyMetaInfo from "@/components/property-details-seller/PropertyMetaInfo";
import PropertyInformations from "@/components/property-details-seller/PropertyInformations";

interface CheckpointResponse {
  description?: string;
}

export default function DetailsProperty() {
  const { id } = useParams();
  const { t } = useTranslation();
  const {
    getProperty,
    removeProperty,
    engineerConnect,
    engineerDisconnect,
    archiveProperty,
    unarchiveProperty,
    getPropertyCheckpoint,
    getPropertyVisits,
  } = useServiceProperty(id);
  const { createOrder } = useServiceShoppingCart(id);
  const [property, setProperty] = useState<AppProperty>();
  const { user } = useAuth();
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalArchive, setOpenModalArchive] = useState(false);
  const [openModalUnarchive, setOpenModalUnarchive] = useState(false);
  const [openModalAddToCart, setOpenModalAddToCart] = useState(false);
  const [openModalCheckpoint, setOpenModalCheckpoint] = useState(false);
  const [propertyVisits, setPropertyVisits] = useState<number>(0);
  const navigate = useNavigate();
  const lng = localStorage.getItem("language");

  const [propertyStatus, setPropertyStatus] = useState<Boolean>(true);
  const [propertyCheckpoint, setPropertyCheckpoint] =
    useState<CheckpointResponse | null>(null);

  const propertyStatuses = [
    "reanalyze_document",
    "legal_analysis",
    "catalog_analysis",
    "analysis",
    "inactive",
  ];

  useEffect(() => {
    async function propertyVisitsGet(propertyId: number) {
      const data = await getPropertyVisits(propertyId);
      setPropertyVisits(data);
    }

    async function fetchProperty() {
      const data = await getProperty();
      setProperty(data);
    }

    async function getPropertyCheckpointData() {
      const data = await getPropertyCheckpoint();
      setPropertyCheckpoint(data);
    }

    function analisePage() {
      if (
        user?.user_type === "engineer" &&
        property?.engineer_connect !== null &&
        property?.engineer_connect !== user?.id
      ) {
        navigate("/news");
      }
    }

    if (!property) {
      fetchProperty();
    }

    if (
      (property && property?.status === "legal_diligence") ||
      property?.status === "rejected"
    ) {
      getPropertyCheckpointData();
    }

    if (property) {
      propertyVisitsGet(property.id);
      analisePage();
      if (propertyStatuses.includes(property.status)) {
        setPropertyStatus(true);
      } else {
        setPropertyStatus(false);
      }
    }
  }, [property]);

  const alterOpenModalDelete = (value: boolean, status: boolean) => {
    setOpenModalDelete(value);

    if (status === true) {
      removeProperty().then(() => {
        navigate("/mySpace");
      });
    }
  };

  const alterOpenModalArchive = (confirmed: boolean) => {
    setOpenModalArchive(false);

    if (confirmed) {
      archiveProperty().then(() => {
        toast.success(t("property-archived"));
        navigate("/mySpace");
      });
    }
  };

  const alterOpenModalUnarchive = (confirmed: boolean) => {
    setOpenModalUnarchive(false);

    if (confirmed) {
      unarchiveProperty().then(() => {
        toast.success(t("property-unarchived"));
        navigate("/mySpace");
      });
    }
  };

  const updateData = async () => {
    const data = await getProperty();
    setProperty(data);
  };

  const addToCart = (value: boolean, status: boolean) => {
    setOpenModalAddToCart(value);

    if (status === true) {
      createOrder().then(() => {
        navigate("/order");
      });
    }
  };

  const images = useMemo(() => {
    const raw = property?.file_images?.split(";") ?? [];
    return raw.filter((s) => typeof s === "string" && s.trim() !== "");
  }, [property?.file_images]);

  return (
    <div className="w-full">
      <ToastContainer />

      {property && (
        <div className="mt-10 mx-10 mb-10 flex flex-col gap-8">
          {/* Parte superior: Galeria + Infos */}
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Galeria */}
            <div className="w-full lg:w-1/2">
              <Gallery images={images} url={storageServerPath} />
            </div>

            {/* Informações */}
            <PropertyInformations
              openModalCheckpoint={() => setOpenModalCheckpoint(true)}
              property={property}
              propertyVisits={propertyVisits}
            />
          </div>

          {/* Botões e Ações */}
          <PropertyMetaInfo
            propertyId={id!}
            property={property}
            propertyStatus={propertyStatus}
            setOpenModalArchive={() => setOpenModalArchive(true)}
            setOpenModalUnarchive={() => setOpenModalUnarchive(true)}
            setOpenModalDelete={() => setOpenModalDelete(true)}
            setOpenModalAddToCart={() => setOpenModalAddToCart(true)}
          />

          {openModalCheckpoint && (
            <div
              className="fixed inset-0 bg-white/60 bg-opacity-50 flex items-center justify-center z-50 !transition-all !duration-300"
              onClick={() => setOpenModalCheckpoint(false)}
            >
              <div
                className="bg-white rounded-lg p-6 max-w-lg w-full shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="!text-2xl !font-bold !mb-4">
                  {t("status-detail")}
                </h2>
                <p>Motivo: {propertyCheckpoint?.description}</p>
                {property?.status === "legal_diligence" && (
                  <p className="!mt-4 font-bold">
                    Atualize sua propriedade para dar prosseguimento ao cadastro
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <button
                    className="!mt-6 !bg-orange-500 !text-white !font-medium !px-4 !py-2 !rounded !hover:bg-blue-700"
                    onClick={() => setOpenModalCheckpoint(false)}
                  >
                    {t("close")}
                  </button>

                  {property.status === "legal_diligence" && (
                    <button
                      className="!mt-6 !bg-blue-600 !text-white !font-medium !px-4 !py-2 !rounded !hover:bg-blue-700"
                      onClick={() => navigate(`/updateProperty/${property.id}`, {
                        state: {readonly: true, isEditing: false}
                      })}
                    >
                      {t("update")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Descrição */}
          <div className="flex flex-col gap-4 border rounded-lg p-6">
            <h1 className="!text-4xl !font-medium !text-[#50700F] w-full bg-gray-200 p-4 rounded-2xl">
              {lng === "en" ? property.name_en : property.name}
            </h1>
            <h2 className="!text-[#50700F] !text-2xl !font-medium underline">
              {t("details")}
            </h2>
            <p className="text-lg whitespace-pre-wrap text-gray-700">
              {lng === "en" ? property.description_en : property.description}
            </p>
          </div>
        </div>
      )}

      <ModalConfirmation
        show={openModalDelete}
        alterOpenModalDelete={alterOpenModalDelete}
        message={t("delete-confirmation")}
      />
      <ModalConfirmation2
        show={openModalAddToCart}
        alterOpenModalDelete={addToCart}
        message={t("can-add-to-cart")}
      />
      <ModalArchive
        show={openModalArchive}
        onClose={alterOpenModalArchive}
        message={t("archive-confirmation")}
      />
      <ModalUnarchive
        show={openModalUnarchive}
        onClose={alterOpenModalUnarchive}
        message={t("unarchive-confirmation")}
      />
      <Footer />
    </div>
  );
}

/*
  const openMapBiomas = () => {
    navigate(`/mapBiomas?lat=${property?.lat}&lng=${property?.lng}`);
  };
  const engineerConnectButton = async () => {
    await engineerConnect().then((data: any) => {
      toast.success(t("successfully"), {
        position: "top-right",
      });
      updateData();
    });
  };
  const engineerDisconnectButton = async () => {
    await engineerDisconnect().then((data: any) => {
      toast.success(t("successfully"), {
        position: "top-right",
      });
      updateData();
    });
  };
  const Nl2br = ({ text }: { text: string }) => {
    const html = text.replace(/\n/g, "<br />");
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };
*/
